import { moveToFulfilled } from '@ms/yammer-data-support';
import { Reducer } from 'redux';

import { AllCampaignsLoadFulfilledAction } from '../../domains/allCampaigns/loadActions.types';
import { CampaignsListLoadMoreFulfilledAction } from '../../domains/allCampaigns/loadMoreAllCampaignsActions.types';
import { CampaignEditFulfilledAction, CampaignPublishFulfilledAction } from '../../domains/campaignEdit/actions.types';
import { CampaignLoadFulfilledAction } from '../../domains/campaigns/actions.types';
import {
  FeedLoadFulfilledAction,
  FeedLoadNewerThreadsFulfilledAction,
  FeedLoadOlderThreadsFulfilledAction,
} from '../../domains/feedLoad/actions.types';
import { MyLeaderSponsoredCampaignsLoadFulfilledAction } from '../../domains/myLeaderSponsoredCampaigns/actions.types';
import {
  NestedThreadDeepLinkSecondLevelLoadFulfilledAction,
  NestedThreadDeepLinkTopLevelLoadFulfilledAction,
  NestedThreadLoadFulfilledAction,
} from '../../domains/nestedThread/types';
import { NestedThreadListLoadFulfilledAction } from '../../domains/nestedThreadList/actions.types';
import { SearchLoadFulfilledAction } from '../../domains/searches/actions.types';
import {
  SecondLevelRepliesLoadLastFulfilledAction,
  SecondLevelRepliesLoadNextFulfilledAction,
  SecondLevelRepliesLoadPreviousFulfilledAction,
} from '../../domains/secondLevelReplies/paginateActions.types';
import {
  TopLevelRepliesLoadInitialFulfilledAction,
  TopLevelRepliesLoadLastFulfilledAction,
  TopLevelRepliesLoadNextFulfilledAction,
  TopLevelRepliesLoadPreviousFulfilledAction,
} from '../../domains/topLevelReplies/paginateActions.types';
import { TrendingCampaignsLoadFulfilledAction } from '../../domains/trendingCampaigns/loadActions.types';
import { TypeaheadFulfilledAction } from '../../domains/typeahead/actions.types';
import { UserSidebarLoadFulfilledAction } from '../../domains/userSidebarLoad/actions.types';

import { CampaignReferences } from './types';

export type CampaignReferencesAction =
  | FeedLoadFulfilledAction
  | FeedLoadNewerThreadsFulfilledAction
  | FeedLoadOlderThreadsFulfilledAction
  | NestedThreadLoadFulfilledAction
  | NestedThreadDeepLinkTopLevelLoadFulfilledAction
  | NestedThreadDeepLinkSecondLevelLoadFulfilledAction
  | NestedThreadListLoadFulfilledAction
  | TopLevelRepliesLoadInitialFulfilledAction
  | TopLevelRepliesLoadLastFulfilledAction
  | TopLevelRepliesLoadPreviousFulfilledAction
  | TopLevelRepliesLoadNextFulfilledAction
  | SearchLoadFulfilledAction
  | SecondLevelRepliesLoadPreviousFulfilledAction
  | SecondLevelRepliesLoadNextFulfilledAction
  | SecondLevelRepliesLoadLastFulfilledAction
  | UserSidebarLoadFulfilledAction
  | AllCampaignsLoadFulfilledAction
  | CampaignLoadFulfilledAction
  | MyLeaderSponsoredCampaignsLoadFulfilledAction
  | CampaignEditFulfilledAction
  | CampaignPublishFulfilledAction
  | CampaignsListLoadMoreFulfilledAction
  | TypeaheadFulfilledAction
  | TrendingCampaignsLoadFulfilledAction;

const initialState: CampaignReferences = {
  pending: { byId: {} },
  fulfilled: { byId: {} },
  rejected: { byId: {} },
};

const reducer: Reducer<CampaignReferences, CampaignReferencesAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'FEED.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.campaignReferences);
    case 'FEED.LOAD_NEWER_THREADS_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.campaignReferences);
    case 'FEED.LOAD_OLDER_THREADS_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.campaignReferences);
    case 'NESTED_THREAD.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.campaignReferences);
    case 'NESTED_THREAD.LOAD_DEEP_LINK_TOP_LEVEL_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.campaignReferences);
    case 'NESTED_THREAD.LOAD_DEEP_LINK_SECOND_LEVEL_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.campaignReferences);
    case 'NESTED_THREAD_LIST.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.campaignReferences);
    case 'TOP_LEVEL_REPLIES.LOAD_INITIAL_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.campaignReferences);
    case 'TOP_LEVEL_REPLIES.LOAD_PREVIOUS_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.campaignReferences);
    case 'TOP_LEVEL_REPLIES.LOAD_NEXT_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.campaignReferences);
    case 'TOP_LEVEL_REPLIES.LOAD_LAST_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.campaignReferences);
    case 'SEARCH.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.campaignReferences);
    case 'SECOND_LEVEL_REPLIES.LOAD_PREVIOUS_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.campaignReferences);
    case 'SECOND_LEVEL_REPLIES.LOAD_NEXT_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.campaignReferences);
    case 'SECOND_LEVEL_REPLIES.LOAD_LAST_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.campaignReferences);
    case 'USER_SIDEBAR.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.campaignReferences);
    case 'ALL_CAMPAIGNS.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.campaignReferences);
    case 'CAMPAIGN.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.campaignReferences);
    case 'ALL_CAMPAIGNS.LOAD_MORE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.campaignReferences);
    case 'CAMPAIGN.EDIT_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.campaignReferences);
    case 'CAMPAIGN.PUBLISH_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.campaignReferences);
    case 'MY_LEADER_SPONSORED_CAMPAIGNS.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.campaignReferences);
    case 'TYPEAHEAD.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.campaignReferences);
    case 'TRENDING_CAMPAIGNS.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.campaignReferences);
    default:
      return state;
  }
};

export default reducer;
